import React from 'react';
import { StyleSheet, View } from 'react-native';
import {
  BodyText,
  Button,
  TextLink,
  theme,
  TitleText,
  useBreakpointHelpers,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import {
  useKustomerAvailability,
  useKustomerContactUsHandler,
} from '../../integrations/kustomer';

export const OrderStatusContactUs = () => {
  const { currentBreakpoint } = useBreakpointHelpers();
  const isKustomerAvailable = useKustomerAvailability();
  const { handleContactUs, handlingContactUs } = useKustomerContactUsHandler(
    isKustomerAvailable,
    'order-status',
  );

  const props: ContactUsProps = {
    onContactUsPress: handleContactUs,
    loading: handlingContactUs,
  };

  if (currentBreakpoint.isMobile) return <MobileContactUs {...props} />;

  return <DesktopContactUs {...props} />;
};

const DesktopContactUs = ({ onContactUsPress, loading }: ContactUsProps) => {
  const { t } = useLocalizationContext();

  return (
    <View style={[styles.contactUsContainerDesktop, styles.row]}>
      <BodyText style={styles.contactUsText}>
        <BodyText size={4} bold>
          {t('order-status-info.contact-us-question')}
        </BodyText>
        <TextLink
          style={[styles.contactUsText, styles.contactUsCta]}
          size={4}
          disabled={loading}
          onPress={onContactUsPress}
        >
          {t('order-status-info.contact-us-cta')}
        </TextLink>
      </BodyText>
    </View>
  );
};

const MobileContactUs = ({ onContactUsPress, loading }: ContactUsProps) => {
  const { t } = useLocalizationContext();

  return (
    <>
      <View style={styles.contactUsDividerMobile} />
      <View style={styles.contactUsContainerMobile}>
        <TitleText style={styles.contactUsTitle} size={5}>
          {t('order-status-info.contact-us-question')}
        </TitleText>
        <BodyText style={styles.contactUsTextMobile} size={4}>
          {t('order-status-info.contact-us-answer-mobile-1')}
        </BodyText>
        <Button
          palette="primary"
          style={styles.mobileContactUsButton}
          onPress={onContactUsPress}
          isLoading={loading}
        >
          {t('order-status-info.contact-us-cta')}
        </Button>
      </View>
    </>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
  },
  contactUsContainerDesktop: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'flex-start',
    borderTopWidth: 1,
    borderTopColor: theme.colors.GREEN_1,
    backgroundColor: theme.colors.KHAKI_2,
    padding: theme.spacing.large,
  },
  contactUsContainerMobile: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'flex-start',
    borderRadius: theme.radius.medium,
    backgroundColor: theme.colors.KHAKI_2,
    padding: theme.spacing.large,
  },
  contactUsDividerMobile: {
    marginTop: theme.spacing.large,
    marginBottom: theme.spacing.xlarge,
    backgroundColor: theme.colors.NEUTRAL_3,
    height: 1,
    width: '100%',
  },
  contactUsTitle: {
    marginBottom: theme.spacing.small,
  },
  contactUsTextMobile: {
    lineHeight: 20,
    textAlign: 'center',
  },
  contactUsText: {
    lineHeight: 20,
  },
  contactUsCta: {
    marginHorizontal: theme.spacing.xsmall,
  },
  mobileContactUsButton: {
    marginTop: theme.spacing.small + theme.spacing.xsmall,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ContactUsProps = {
  readonly onContactUsPress: () => void;
  readonly loading: boolean;
};
